import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../../context/app';
import type { Place, SatellitePlace } from '../../../context/types';
import { ThemeType } from '../../../context/types';
import { isSatellitePlace } from '../../../utils/typeGuards';
import ButtonStyled from '../button/Button';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import styles from './locationSelector.module.scss';

interface LocationListItemProps {
  place: Place | SatellitePlace;
  activeLocation: number | null;
  setLocation: (v: number) => void;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  setting: 'starting' | 'destination';
}

const LocationListItem: React.FC<LocationListItemProps> = ({
  place,
  activeLocation,
  setLocation,
  onClick,
  setting,
}) => {
  const isActive = activeLocation === place.id;
  const { availableSites } = useContext(AppContext);
  const { t } = useTranslation('common');

  const site = useMemo(
    () =>
      availableSites?.find(
        (site) => site.id === +(place as Place).location?.site,
      ),
    [availableSites, place],
  );

  const building = useMemo(() => {
    if (site) {
      return site.buildings.find(
        (b) => b.id === (place as Place).location?.building,
      );
    }
  }, [site, place]);

  const floor = useMemo(() => {
    if (building) {
      return building?.floors
        .sort((a, b) => a.id - b.id)
        .find((f) => f.id === (place as Place).location?.floor);
    }
  }, [building, place]);

  return (
    <div
      className={`${styles.locationListItem} ${
        isActive && styles.locationListItemActive
      }`}
      onClick={onClick}
      role="button"
    >
      {isActive ? (
        <>
          <div className={styles.locationListItemActiveSub}>
            <div className={styles.iconColumn}>
              <Icon icon={IconType.LOCATION} theme={ThemeType.PRIMARY} />
              <Icon icon={IconType.BUILDING} theme={ThemeType.PRIMARY} />
            </div>
            <div className={styles.contentColumn}>
              <div className={styles.boldTitle}>{place.name}</div>
              <div className={styles.subtitle}>
                {site?.name || site?.short_name}
              </div>
              {isSatellitePlace(place) ? (
                <div className={styles.title}>
                  {(place as SatellitePlace).formatted_display_address}
                </div>
              ) : (
                <div className={styles.title}>
                  {`${building?.name}, ${floor?.name}`}
                </div>
              )}
            </div>
          </div>
          <ButtonStyled onClick={() => setLocation(place.id)} uppercase>
            {setting === 'starting'
              ? t('campusNavigation.form.setStartingLocation')
              : t('campusNavigation.form.setDestination')}
          </ButtonStyled>
        </>
      ) : (
        <>
          <div className={styles.iconColumn}>
            <Icon icon={IconType.LOCATION_OPEN} theme={ThemeType.PRIMARY} />
          </div>
          <div className={styles.contentColumn}>
            <span className={styles.title}>{place.name}</span>
            <div className={styles.subtitle}>
              {site?.name || site?.short_name}
            </div>
          </div>
        </>
      )}
      <div className={styles.locationListCarat}>
        <Icon icon={isActive ? IconType.UP : IconType.DOWN} />
      </div>
    </div>
  );
};

export default LocationListItem;
